import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getImageOrientation } from '../lib/image-orientation';

gsap.registerPlugin(ScrollTrigger);

let lenis: Lenis | undefined;
let lenisTick: ((time: number) => void) | undefined;
let motionContext: gsap.Context | undefined;
let interactionController: AbortController | undefined;
let collageController: AbortController | undefined;
let contactFormController: AbortController | undefined;
let lightboxController: AbortController | undefined;

// Amplitudes (x/y) reduzidas a ~45% do original (era 11–21px) — o movimento
// ficava com margem a mais, a ponto de parecer descontrolado; o pedido foi
// mais contido, não removido.
const collageFloatVectors = [
	{ x: 9, y: -6, rotation: 1.8, duration: 5.4, delay: 0 },
	{ x: -7, y: 8, rotation: -1.4, duration: 6.7, delay: 0.55 },
	{ x: 6, y: 9, rotation: 1.2, duration: 5.9, delay: 1.15 },
	{ x: -9, y: -5, rotation: -1.7, duration: 7.2, delay: 0.25 },
	{ x: 8, y: -8, rotation: 1.5, duration: 6.3, delay: 1.45 },
	{ x: -5, y: 7, rotation: -1.1, duration: 5.7, delay: 0.85 },
	// Vetores 7 e 8 para as duas imagens da faixa direita: sem eles o
	// `index % collageFloatVectors.length` reciclava os dois primeiros e as
	// novas flutuavam exatamente em sincronia com as duas da ponta esquerda.
	{ x: 7, y: 8, rotation: 1.35, duration: 6.9, delay: 0.4 },
	{ x: -8, y: -6, rotation: -1.6, duration: 6.1, delay: 1.7 },
] as const;

function revealWithoutMotion(root: HTMLElement) {
	root.querySelectorAll<HTMLElement>('[data-hero-line], [data-hero-media], [data-kinetic-reveal], [data-value-item]').forEach((element) => {
		gsap.set(element, { clearProps: 'all', opacity: 1, visibility: 'visible' });
	});
	root.querySelectorAll<HTMLElement>('[data-portfolio-cell]').forEach((element) => {
		gsap.set(element, { clearProps: 'all', x: 0 });
	});
}

function initAccordion(root: HTMLElement) {
	interactionController = new AbortController();
	const { signal } = interactionController;

	root.querySelectorAll<HTMLElement>('[data-service]').forEach((service) => {
		const trigger = service.querySelector<HTMLButtonElement>('.kinetic-service__trigger');
		if (!trigger) return;

		trigger.addEventListener('click', () => {
			const nextOpen = !service.classList.contains('is-open');
			root.querySelectorAll<HTMLElement>('[data-service].is-open').forEach((openService) => {
				openService.classList.remove('is-open');
				openService.querySelector<HTMLButtonElement>('.kinetic-service__trigger')?.setAttribute('aria-expanded', 'false');
			});
			service.classList.toggle('is-open', nextOpen);
			trigger.setAttribute('aria-expanded', String(nextOpen));
		}, { signal });
	});
}

// Mesma lógica do formulário de contactos.astro (site estático, sem
// servidor próprio — em vez de um endpoint a receber o POST, constrói um
// mailto: com os dados preenchidos). Centralizado aqui em vez de um
// <script> próprio na página de contactos cinética, para reutilizar o
// mesmo ciclo de vida (AbortController + astro:page-load/before-swap) já
// estabelecido para todo o resto do template — só corre se a página tiver
// o formulário (gate por querySelector, o padrão já usado por
// initCollage/initAccordion para não rebentar nas páginas que não o têm).
function initContactForm(root: HTMLElement) {
	const form = root.querySelector<HTMLFormElement>('[data-kinetic-contact-form]');
	if (!form) return;

	contactFormController = new AbortController();
	const { signal } = contactFormController;
	const status = root.querySelector<HTMLElement>('[data-kinetic-form-status]');
	const EMAIL = 'geral@agenciapontoevirgula.com';

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		if (!form.reportValidity()) return;

		const data = new FormData(form);
		const name = String(data.get('name') ?? '').trim();
		const email = String(data.get('email') ?? '').trim();
		const message = String(data.get('message') ?? '').trim();

		const subject = `Contacto via site — ${name}`;
		const body = `Nome: ${name}\nEmail: ${email}\n\nMensagem:\n${message}`;
		const mailtoUrl = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

		window.location.href = mailtoUrl;

		if (status) {
			status.textContent = 'A abrir o seu programa de email com a mensagem pronta a enviar…';
			status.classList.add('is-visible');
		}
	}, { signal });
}

// Lightbox da página individual de projeto (single-projeto.php, WordPress
// — não existe no protótipo Astro, o mosaico da home/portfólio não abre
// nada). Porta a mesma lógica de src/pages/portfolio/[slug].astro
// (site real), adaptada às classes .kinetic-lightbox/.kinetic-project-cell
// (kinetic.css) — gate por querySelector, como initCollage/
// initContactForm, para não fazer nada nas páginas sem galeria.
function initLightbox(root: HTMLElement) {
	const lightbox = root.querySelector<HTMLElement>('[data-lightbox]');
	const imgEl = root.querySelector<HTMLImageElement>('[data-lightbox-img]');
	const triggers = Array.from(root.querySelectorAll<HTMLElement>('[data-lightbox-open]'));
	if (!lightbox || !imgEl || triggers.length === 0) return;

	lightboxController = new AbortController();
	const { signal } = lightboxController;
	const prevBtn = root.querySelector<HTMLElement>('[data-lightbox-prev]');
	const nextBtn = root.querySelector<HTMLElement>('[data-lightbox-next]');
	const closeEls = root.querySelectorAll<HTMLElement>('[data-lightbox-close]');
	const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	const sources = triggers.map((t) => t.dataset.fullSrc ?? '');
	const baseAlt = triggers[0]?.dataset.altBase ?? '';
	let currentIndex = 0;
	let lastFocused: HTMLElement | null = null;

	const setImage = (index: number) => {
		currentIndex = (index + sources.length) % sources.length;
		imgEl.src = sources[currentIndex];
		imgEl.alt = `${baseAlt} — imagem ${currentIndex + 1}`;
	};

	const open = (index: number, trigger: HTMLElement) => {
		lastFocused = trigger;
		setImage(index);
		lightbox.classList.add('is-open');
		lightbox.setAttribute('aria-hidden', 'false');
		document.body.style.overflow = 'hidden';
		lenis?.stop();

		if (reducedMotion) {
			gsap.set(lightbox, { autoAlpha: 1 });
			gsap.set(imgEl, { autoAlpha: 1, scale: 1, y: 0 });
		} else {
			gsap.killTweensOf([lightbox, imgEl]);
			gsap.fromTo(lightbox, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });
			gsap.fromTo(imgEl, { autoAlpha: 0, scale: 0.92, y: 24 }, { autoAlpha: 1, scale: 1, y: 0, duration: 0.5, delay: 0.05, ease: 'power3.out' });
		}
		root.querySelector<HTMLElement>('[data-lightbox-close]')?.focus();
	};

	const close = () => {
		document.body.style.overflow = '';
		lenis?.start();
		lightbox.setAttribute('aria-hidden', 'true');

		if (reducedMotion) {
			gsap.set(lightbox, { autoAlpha: 0 });
			lightbox.classList.remove('is-open');
		} else {
			gsap.killTweensOf([lightbox, imgEl]);
			gsap.to(imgEl, { autoAlpha: 0, scale: 0.94, y: 16, duration: 0.28, ease: 'power2.in' });
			gsap.to(lightbox, { autoAlpha: 0, duration: 0.32, delay: 0.05, ease: 'power2.in', onComplete: () => lightbox.classList.remove('is-open') });
		}
		lastFocused?.focus();
	};

	const go = (delta: number) => {
		setImage(currentIndex + delta);
		if (!reducedMotion) {
			gsap.fromTo(imgEl, { autoAlpha: 0.4, x: delta > 0 ? 16 : -16 }, { autoAlpha: 1, x: 0, duration: 0.35, ease: 'power2.out' });
		}
	};

	triggers.forEach((trigger, i) => trigger.addEventListener('click', () => open(i, trigger), { signal }));
	closeEls.forEach((el) => el.addEventListener('click', close, { signal }));
	prevBtn?.addEventListener('click', () => go(-1), { signal });
	nextBtn?.addEventListener('click', () => go(1), { signal });
	document.addEventListener('keydown', (event) => {
		if (lightbox.getAttribute('aria-hidden') !== 'false') return;
		if (event.key === 'Escape') close();
		if (event.key === 'ArrowLeft') go(-1);
		if (event.key === 'ArrowRight') go(1);
	}, { signal });
}

function initCollage(root: HTMLElement) {
	const collage = root.querySelector<HTMLElement>('[data-collage]');
	if (!collage) return;

	collageController = new AbortController();
	const { signal } = collageController;
	const items = Array.from(collage.querySelectorAll<HTMLElement>('[data-collage-item]'));

	items.forEach((item) => {
		const image = item.querySelector<HTMLImageElement>('img');
		if (!image) return;

		// A moldura adapta-se à imagem, não o contrário: além da categoria
		// (portrait/landscape/square, que decide quanto espaço ocupa na
		// composição), escreve-se o rácio EXATO em --socket-ratio. Com o
		// aspect-ratio da moldura igual ao da imagem, não há corte nem barras
		// laterais seja qual for o ficheiro que o cliente venha a pôr aqui —
		// as três categorias sozinhas ainda deixavam barras (uma foto 16:9
		// dentro de uma moldura 5:4, por exemplo).
		const syncOrientation = () => {
			const { naturalWidth: w, naturalHeight: h } = image;
			item.dataset.orientation = getImageOrientation(w, h);
			if (w > 0 && h > 0) item.style.setProperty('--socket-ratio', `${w} / ${h}`);
		};

		image.addEventListener('load', syncOrientation, { signal });
		if (image.complete) syncOrientation();
	});

}

function initMotion(root: HTMLElement) {
	const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	if (reducedMotion) {
		revealWithoutMotion(root);
		return;
	}

	lenis = new Lenis({ anchors: true });
	lenis.on('scroll', ScrollTrigger.update);
	lenisTick = (time: number) => lenis?.raf(time * 1000);
	gsap.ticker.add(lenisTick);
	gsap.ticker.lagSmoothing(0);

	motionContext = gsap.context(() => {
		// Estado inicial: escondido explicitamente via GSAP (não depende de CSS
		// separado permanecer sincronizado com immediateRender:false abaixo).
		gsap.set('[data-hero-line]', { autoAlpha: 0, y: 24 });
		gsap.set('[data-hero-media]', { autoAlpha: 0, scale: 0.9, rotate: 1 });
		gsap.set('[data-kinetic-reveal]', { autoAlpha: 0, y: 42 });
		gsap.set('[data-value-item]', { autoAlpha: 0, y: 28 });

		const heroTimeline = gsap.timeline({ defaults: { ease: 'power4.out' } });
		heroTimeline
			.to('[data-hero-line]', {
				autoAlpha: 1,
				y: 0,
				duration: 0.65,
				stagger: 0.06,
			})
			.to('[data-hero-media]', {
				autoAlpha: 1,
				scale: 1,
				rotate: 7,
				duration: 0.85,
				ease: 'power3.out',
			}, '-=0.58');

		const reveals = gsap.utils.toArray<HTMLElement>('[data-kinetic-reveal]');
		reveals.forEach((element) => {
			gsap.to(element, {
				autoAlpha: 1,
				y: 0,
				duration: 0.9,
				ease: 'power3.out',
				scrollTrigger: {
					trigger: element,
					start: 'top 86%',
					once: true,
				},
			});
		});

		// Página de portfólio: cada [data-portfolio-project] tem a sua própria
		// faixa de imagens (não uma imagem só) — cada uma precisa do seu
		// PRÓPRIO grupo de stagger, por isso não dá para reutilizar o
		// mecanismo de [data-values-reveal] (só suporta um trigger).
		const portfolioProjects = gsap.utils.toArray<HTMLElement>('[data-portfolio-project]');
		portfolioProjects.forEach((project) => {
			const domCells = Array.from(project.querySelectorAll<HTMLElement>('[data-portfolio-cell]'));
			if (domCells.length > 0) {
				// A ordem de CHEGADA (quem o stagger anima primeiro) não é
				// sempre a ordem do DOM (esquerda→direita) — depende do lado
				// de entrada. A primeira a chegar tem de ser sempre a célula
				// do lado "cortado" da diagonal (o espaço mais pequeno em
				// altura da triangulação — ver .kinetic-portfolio-project--a/
				// --b em kinetic.css): --a corta a ESQUERDA (entra pela
				// direita) → ordem do DOM já serve, esquerda chega primeiro.
				// --b corta a DIREITA (entra pela esquerda) → tem de ser o
				// INVERSO do DOM, direita chega primeiro. Bug real, apanhado
				// pelo utilizador nas secções que entram pela esquerda
				// ("Estúdio Cerâmica Norte" etc.): a esquerda chegava
				// primeiro em todas, certo só para as que entram pela
				// direita.
				const cells = project.dataset.entry === 'left' ? [...domCells].reverse() : domCells;
				// O estado inicial (translateX 100vw/-100vw, consoante
				// data-entry) já vem do CSS (backstop) — aqui só se garante
				// que o GSAP começa exatamente desse valor antes de animar,
				// para não haver salto entre o que o CSS mostrava e o que o
				// GSAP assume como ponto de partida. Tem de ser vw, não %
				// (que é relativo à largura da PRÓPRIA célula, 1/3 da
				// secção) — ver comentário em kinetic.css.
				const fromX = project.dataset.entry === 'left' ? '-100vw' : '100vw';
				gsap.set(cells, { x: fromX });
				// "Vêm sempre com algum distanciamento": não é um gap espacial
				// entre as imagens (o flexbox já as deixa encostadas, sem
				// espaço), é o desfasamento no TEMPO de chegada — a primeira
				// célula (a do espaço mais pequeno da diagonal) chega
				// primeiro; a segunda só é vista a chegar depois de a
				// primeira já lá estar, e assim sucessivamente.
				gsap.to(cells, {
					x: 0,
					// Mais lento, pedido explícito (2026-09-04) — era
					// duration: 1 / stagger: 0.16. O desfasamento entre
					// células cresceu na mesma proporção da duração, para
					// a "distância" de chegada entre a 1ª/2ª/3ª continuar a
					// ler-se, não só a viagem de cada uma ficar mais lenta.
					duration: 1.8,
					stagger: 0.28,
					ease: 'power3.out',
					// "o evento começa quando a secção começa a aparecer no
					// ecrã" — start no fundo da janela, não a meio como o
					// resto dos reveals (top 75-86%).
					scrollTrigger: { trigger: project, start: 'top bottom', once: true },
				});
			}
		});

		// Lista de valores da secção 2: antes cada <li> tinha o data-kinetic-reveal
		// genérico, cada um com o seu próprio ScrollTrigger — sem ligação entre
		// eles, entravam praticamente ao mesmo tempo (a lista é curta, os 4 itens
		// cruzam o limiar de scroll quase no mesmo instante). Stagger dedicado
		// para ficar visivelmente escalonado (auditoria de design, 2026-09-03).
		// Sem clearProps aqui: ao contrário da colagem (que não tem backstop CSS
		// próprio), [data-value-item] tem `opacity:0` fixo no kinetic.css como
		// rede de segurança antes do JS correr — clearProps removia o
		// opacity:1 inline assim que a animação acabasse e a regra CSS
		// reafirmava-se por baixo, escondendo tudo outra vez de repente. Mesmo
		// padrão que [data-kinetic-reveal] já usa, pela mesma razão.
		const valueItems = gsap.utils.toArray<HTMLElement>('[data-value-item]');
		if (valueItems.length > 0) {
			gsap.to(valueItems, {
				autoAlpha: 1,
				y: 0,
				duration: 0.75,
				stagger: 0.09,
				ease: 'power3.out',
				scrollTrigger: {
					trigger: '[data-values-reveal]',
					start: 'top 85%',
					once: true,
				},
			});
		}

		const collageItems = gsap.utils.toArray<HTMLElement>('[data-collage-item]');
		// Flutuação contínua só faz sentido com rato (parallax fino) e só enquanto
		// a colagem está no ecrã — evita tweens a correr para sempre em mobile ou
		// fora de vista.
		const allowCollageFloat = window.matchMedia('(pointer: fine)').matches;
		const collageFloatTweens = allowCollageFloat
			? collageItems.map((item, index) => {
					const vector = collageFloatVectors[index % collageFloatVectors.length];
					return gsap.to(item, {
						x: vector.x,
						y: vector.y,
						rotation: `+=${vector.rotation}`,
						duration: vector.duration,
						delay: vector.delay,
						repeat: -1,
						yoyo: true,
						ease: 'sine.inOut',
						paused: true,
					});
				})
			: [];

		// Sem "rotate" aqui de propósito: cada item já tem a sua própria rotação
		// fixa via CSS (transform: rotate(...) em .kinetic-collage__item--*).
		// Se o GSAP também escrevesse rotate:0 (como acontecia antes), ficava a
		// sobrepor-se a esse transform (inline vence sempre a classe) durante
		// toda a entrada — a imagem entrava direita, sem rotação nenhuma — e só
		// ao fim, quando o clearProps limpava o inline, é que saltava de repente
		// para o ângulo certo: exatamente o "aos saltos" que se via na entrada.
		gsap.set(collageItems, { autoAlpha: 0, y: 90 });
		gsap.to(collageItems, {
			autoAlpha: 1,
			y: 0,
			duration: 1.15,
			stagger: 0.13,
			ease: 'power4.out',
			clearProps: 'transform,opacity,visibility',
			onComplete: () => collageFloatTweens.forEach((tween) => tween.play()),
			scrollTrigger: {
				trigger: '[data-collage]',
				start: 'top 74%',
				once: true,
			},
		});

		if (collageFloatTweens.length > 0) {
			ScrollTrigger.create({
				trigger: '[data-collage]',
				start: 'top bottom',
				end: 'bottom top',
				onLeave: () => collageFloatTweens.forEach((tween) => tween.pause()),
				onLeaveBack: () => collageFloatTweens.forEach((tween) => tween.pause()),
				onEnterBack: () => collageFloatTweens.forEach((tween) => tween.resume()),
			});
		}

		root.querySelectorAll<HTMLElement>('[data-symbol]').forEach((element) => {
			const angle = Number(element.dataset.angle ?? 0) * (Math.PI / 180);
			const isFooterSymbol = element.closest('.kinetic-footer') !== null;
			const amplitude = isFooterSymbol ? 22 : 14;
			const duration = Number(element.dataset.duration ?? 8) * (isFooterSymbol ? 0.48 : 1);
			gsap.to(element, {
				x: Math.cos(angle) * amplitude,
				y: Math.sin(angle) * amplitude,
				duration,
				delay: Number(element.dataset.delay ?? 0),
				repeat: -1,
				yoyo: true,
				ease: 'sine.inOut',
			});
		});

		const header = document.querySelector<HTMLElement>('[data-kinetic-header]');
		if (header) {
			ScrollTrigger.create({
				trigger: root,
				start: 'top -48',
				end: 'bottom bottom',
				toggleClass: { targets: header, className: 'is-scrolled' },
			});
		}
	}, root);

}

function cleanupKineticTemplate() {
	interactionController?.abort();
	interactionController = undefined;
	collageController?.abort();
	collageController = undefined;
	contactFormController?.abort();
	contactFormController = undefined;
	lightboxController?.abort();
	lightboxController = undefined;
	motionContext?.revert();
	motionContext = undefined;
	if (lenisTick) {
		gsap.ticker.remove(lenisTick);
		lenisTick = undefined;
	}
	lenis?.destroy();
	lenis = undefined;
	gsap.ticker.lagSmoothing(500, 33);
}

export function initKineticTemplate() {
	cleanupKineticTemplate();
	const root = document.querySelector<HTMLElement>('[data-kinetic-page]');
	if (!root) return;
	initAccordion(root);
	initCollage(root);
	initContactForm(root);
	initLightbox(root);
	initMotion(root);
}

export { cleanupKineticTemplate };

// A ligação ao ciclo de vida (quando initKineticTemplate/cleanupKineticTemplate
// correm) NÃO vive aqui — este ficheiro é partilhado entre o protótipo Astro
// (astro-kinetic-entry.ts, liga a astro:page-load/before-swap, porque o
// ClientRouter troca conteúdo sem recarregar a página) e o tema WordPress
// (wp-kinetic-entry.ts, liga a DOMContentLoaded — o WordPress recarrega a
// página inteira em cada navegação, não há "swap" a limpar). Manter as duas
// ligações em ficheiros à parte evita que uma delas corra a lógica duas
// vezes por engano (ex.: astro:page-load E DOMContentLoaded no mesmo
// documento disparariam ambos no carregamento inicial).
